import { getStaticDomain, isAuthorMode } from '../utils/aem-utils';
import { isMobile } from '../utils/device-utils';
import { LAZY_LOADED_CSS_SELECTORS } from '../../config/css-config.mjs';

const LAZY_CLASSNAME_SELECTOR = 'img.j-lazyload, iframe[data-src]';

const processSrc = (element) => {
  const $element = element.target || element;
  if ($element.dataset?.lazyimgmobile && $element.dataset?.lazyimgdesktop) {
    $element.setAttribute('src', isMobile() ? $element.dataset.lazyimgmobile : $element.dataset.lazyimgdesktop);
  } else {
    if ($element.dataset?.src) {
      $element.setAttribute('src', $element.dataset.src);
    }
    if ($element.dataset?.srcset) {
      $element.setAttribute('srcset', $element.dataset.srcset);
    }
  }
};

const loadCss = (component) => {
  // VERSION is injected at build time by webpack.DefinePlugin from package.json
  const version = process.env.VERSION || '1.3.150'; // Fallback random version
  const cssUrl = `${getStaticDomain()}/libs/sky-aem-frontend/clientlibs/css_lib_lazy_css_components/${version}/css/${component}.css`;

  if (!document.querySelector(`link[href="${cssUrl}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl;
    document.head.appendChild(link);
  }
};

const observerOptions = {
  root: null,
  rootMargin: '500px',
  threshold: 0.1,
};

const lazyObserverCallback = (entries, lazyObserver) => {
  entries.forEach((entry) => {
    if (isAuthorMode()) {
      processSrc(entry);
      lazyObserver.unobserve(entry.target);
    } else if (entry.isIntersecting) {
      processSrc(entry);
      lazyObserver.unobserve(entry.target);
    }
  });
};

const lazyObserverCSSCallback = (entries, lazyObserver) => {
  entries.forEach((entry) => {
    const classList = Array.from(entry.target.classList);
    const cleanSelector = classList.find((cls) => cls.startsWith('c-') && !cls.includes('--'));

    if (cleanSelector) {
      loadCss(cleanSelector);
      lazyObserver.unobserve(entry.target);
    }
  });
};

/**
 * Initialize Content Lazy Loading
 * Manages lazy loading for images and iframes with LAZY_CLASSNAME_SELECTOR
 */
const initLazyContentLoader = async () => {
  console.info('init lazy-content-loader');
  const lazyObserver = new IntersectionObserver(lazyObserverCallback, observerOptions);

  document.querySelectorAll(LAZY_CLASSNAME_SELECTOR).forEach(($lazyElement) => {
    lazyObserver.observe($lazyElement);
  });

  if (isAuthorMode()) {
    // In author mode, we need to watch for dynamically added images and iframes
    const $container = document.querySelector('body');
    const bodyObserver = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          node?.querySelectorAll?.(LAZY_CLASSNAME_SELECTOR)?.forEach(processSrc);
        });
      });
    });
    bodyObserver.observe($container, { childList: true, subtree: true });
  }
};

/**
 * Initialize CSS Lazy Loading
 * Manages lazy loading for CSS based on LAZY_LOADED_CSS_SELECTORS
 */
const initLazyCssLoader = async () => {
  console.info('init lazy-css-loader');
  const lazyCssObserver = new IntersectionObserver(lazyObserverCSSCallback, observerOptions);

  if (LAZY_LOADED_CSS_SELECTORS) {
    LAZY_LOADED_CSS_SELECTORS.forEach((selector) => {
      const cleanSelector = selector
        .toString()
        .replace('/g', '')
        .replace(/[^a-zA-Z-]/g, '');
      const $lazyElement = document.querySelector(`.${cleanSelector}`);
      if ($lazyElement && !isAuthorMode()) {
        lazyCssObserver.observe($lazyElement);
      }
      if ($lazyElement && isAuthorMode()) {
        loadCss(cleanSelector);
      }
    });
  }
};

export { initLazyContentLoader, initLazyCssLoader };
